import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ChevronLeft, Download } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT, inr } from "@/lib/brand";
import { RequireAuth } from "@/components/site/Shared";

function InvoiceInner() {
  const { id } = useParams();
  const api = useApi();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [dl, setDl] = useState(false);
  useEffect(() => {
    api.get(`/bookings/${id}/invoice`).then((r) => setInv(r.data)).catch((e) => setError(errMsg(e)));
  }, [id, api]);

  const download = async () => {
    setDl(true);
    try {
      const r = await api.get(`/bookings/${id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `${inv.invoice_no.replaceAll("/", "-")}.pdf` });
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download the invoice. Please try again.");
    } finally {
      setDl(false);
    }
  };

  if (error) return <div className="container-x py-24 text-center text-rose-700" data-testid="invoice-error">{error}</div>;
  if (!inv) return <div className="container-x py-24 text-neutral-500">Loading invoice…</div>;

  return (
    <div className="container-x max-w-3xl py-10" data-testid="invoice-page">
      <div className="mb-5 flex items-center justify-between print:hidden">
        <Link to="/bookings" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-950"><ChevronLeft className="h-4 w-4" /> Bookings</Link>
        <div className="flex gap-2">
          <button onClick={download} disabled={dl} data-testid="invoice-download-btn" className="inline-flex items-center gap-2 rounded-full bg-[#FF5A00] px-5 py-2.5 text-sm font-semibold text-white transition-opacity duration-200 hover:opacity-90 disabled:opacity-60"><Download className="h-4 w-4" /> {dl ? "Preparing…" : "Download PDF"}</button>
          <button onClick={() => window.print()} data-testid="invoice-print-btn" className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-900 transition-colors duration-200 hover:bg-neutral-50"><Printer className="h-4 w-4" /> Print</button>
        </div>
      </div>
      <div className="rounded-3xl border border-black/10 bg-white p-8 sm:p-10 print:border-0 print:p-0" id="invoice">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-black/10 pb-6">
          <div>
            <p className="font-display text-2xl font-bold">Hoore<span className="text-[#FF5A00]">cars</span><span className="text-sm text-neutral-400">.com</span></p>
            <p className="mt-2 text-sm font-semibold">{inv.seller.name}</p>
            <p className="max-w-xs text-xs leading-5 text-neutral-500">{inv.seller.address}</p>
            <p className="mt-1 text-xs text-neutral-500">GSTIN: {inv.seller.gstin} · CIN: {inv.seller.cin}</p>
            <p className="text-xs text-neutral-500">{inv.seller.email} · {inv.seller.phone}</p>
          </div>
          <div className="text-right">
            <p className="label-eyebrow">Tax Invoice</p>
            <p className="mt-2 font-mono text-sm font-semibold" data-testid="invoice-no">{inv.invoice_no}</p>
            <p className="text-xs text-neutral-500">Date: {fmtDT(inv.invoice_date)}</p>
            <p className="text-xs text-neutral-500">Booking: {inv.booking_id}</p>
            <span className={`mt-2 inline-block rounded-full px-3 py-0.5 text-xs font-semibold ${inv.paid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="invoice-paid">{inv.paid ? "PAID" : "UNPAID"}</span>
          </div>
        </div>
        <div className="grid gap-6 py-6 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Billed to</p>
            <p className="mt-1 font-medium">{inv.buyer.name}</p>
            <p className="text-sm text-neutral-600">{inv.buyer.phone}</p>
            <p className="text-sm text-neutral-600">{inv.buyer.email}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Trip</p>
            <p className="mt-1 font-medium">{inv.trip.car}</p>
            <p className="text-sm text-neutral-600">{fmtDT(inv.trip.pickup_at)} → {fmtDT(inv.trip.return_at)}</p>
            <p className="text-xs text-neutral-500">Place of supply: {inv.place_of_supply}</p>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-y border-black/10 text-left text-xs uppercase tracking-wider text-neutral-500">
            <th className="py-2">Description</th><th className="py-2 text-center">HSN/SAC</th><th className="py-2 text-right">Amount</th>
          </tr></thead>
          <tbody className="divide-y divide-black/5">
            {inv.lines.map((l) => (
              <tr key={l.desc}><td className="py-2.5">{l.desc}</td><td className="py-2.5 text-center text-neutral-500">{inv.hsn}</td><td className="py-2.5 text-right">{inr(l.amount)}</td></tr>
            ))}
          </tbody>
        </table>
        <div className="mt-6 ml-auto w-full max-w-xs space-y-2 text-sm">
          {[["Taxable value", inv.taxable], ["CGST @ 9%", inv.cgst], ["SGST @ 9%", inv.sgst]].map(([k, v]) => (
            <div key={k} className="flex justify-between text-neutral-600"><span>{k}</span><span>{inr(v)}</span></div>
          ))}
          <div className="flex justify-between border-t border-black/10 pt-2 font-semibold"><span>Invoice total</span><span>{inr(inv.taxable + inv.gst)}</span></div>
          <div className="flex justify-between text-neutral-600"><span>Refundable security deposit</span><span>{inr(inv.deposit)}</span></div>
          <div className="flex justify-between border-t border-black/10 pt-2 text-base font-bold"><span>Amount paid</span><span data-testid="invoice-total">{inr(inv.total)}</span></div>
        </div>
        {inv.payment_ref && <p className="mt-6 text-xs text-neutral-500">Payment reference: {inv.payment_ref}</p>}
        <p className="mt-6 border-t border-black/10 pt-4 text-center text-xs text-neutral-400">This is a computer-generated invoice and does not require a signature. The security deposit is refundable after the return inspection as per the Security Deposit Policy.</p>
      </div>
    </div>
  );
}

export default function Invoice() {
  return <RequireAuth><InvoiceInner /></RequireAuth>;
}
